import React, { useEffect, useState } from "react";
import { getConfigCached } from "../staff/formwizApi";
import { eventBus } from "../../utils/eventBus";
import { errInfo } from "../staff/staffUtils";
import { internalApi, patientIdOf } from "./internalApi";
import { downloadBlob, printBlob, sendWithPdf, staffPdfBlob } from "./staffPdf";
import AmendDialog from "./AmendDialog";
import VoidDialog from "./VoidDialog";
import "../staff/staff.css";
import "./internal.css";

// EMR states in which a staff form is locked (edits refused by the server).
export const LOCKED_EMR = ["queued", "posting", "posted"];

// Which buttons a staff form shows (StaffMeta + the practice's EMR switch).
export function internalButtons(detail, { emrOn = true, canEdit = true, canAmendPatient = true } = {}) {
  const d = detail || {};
  const staff = d.staff || {};
  const saved = d.status === "submitted";
  const locked = LOCKED_EMR.includes(d.emr_status);
  const internalKind = staff.kind === "internal" || staff.kind === "amendment";
  return {
    edit: saved && !!staff.editable && canEdit && internalKind,
    pdf: saved,
    print: saved,
    emr: saved && !locked && emrOn && internalKind,
    amend: saved && !!staff.can_amend && (internalKind || canAmendPatient),
    void: saved && !!staff.can_void && internalKind,
    discardChanges: saved && !!staff.pending_edit && !locked,
    discardDraft: d.status === "in_progress" && !!staff.can_discard,
  };
}

/*
 * Actions on a staff-completed form: Edit, Save PDF, Print, Send to EMR,
 * Amend, Void, Discard changes / draft. The server re-checks every one.
 *   detail     InternalDetail (GET /internal/submissions/{id})
 *   def        its definition
 *   onChanged(detail)      after anything changed (the fresh detail)
 *   onEdit()               open the editor (the fill screen)
 *   onOpenSubmission(id, {startEditing})  open another submission (the new amendment draft)
 *   showEmr    false: the caller shows its own Send to EMR (patient submissions)
 */
export default function InternalActions({ detail, def, onChanged, onEdit, onOpenSubmission, showEmr = true, showPrint = true }) {
  const [busy, setBusy] = useState("");
  const [stepText, setStepText] = useState("");
  const [msg, setMsg] = useState(null); // {tone, text}
  const [amendOpen, setAmendOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [dlgError, setDlgError] = useState("");
  const [emrOn, setEmrOn] = useState(true);

  useEffect(() => {
    let alive = true;
    getConfigCached()
      .then((cfg) => {
        const emr = cfg && cfg.practice && cfg.practice.emr;
        if (alive) setEmrOn(!(emr && emr.enabled === false));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!detail) return null;
  const staff = detail.staff || {};
  const show = internalButtons(detail, { emrOn, canEdit: !!onEdit });
  const title = detail.form_title || (def && def.title && def.title.en) || "";

  const refresh = async () => {
    eventBus.emit("formwiz:inbox-changed");
    eventBus.emit("formwiz:staff-forms-changed", { patientid: patientIdOf(detail) });
    try {
      const fresh = await internalApi.get(detail.id);
      if (onChanged) onChanged(fresh);
      return fresh;
    } catch (_) {
      return null;
    }
  };

  const pdf = async (print) => {
    if (!def) return;
    setBusy(print ? "print" : "pdf");
    setMsg(null);
    try {
      const blob = await staffPdfBlob(detail, def);
      if (print) printBlob(blob);
      else downloadBlob(blob, `${detail.form_id}-${detail.id}.pdf`);
    } catch (_) {
      setMsg({ tone: "error", text: "Couldn't make the PDF." });
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    if (!def) return;
    setBusy("emr");
    setMsg(null);
    setStepText("Sending to the EMR…");
    try {
      const summary = await sendWithPdf({ detail, def, onStep: setStepText, reload: () => internalApi.get(detail.id) });
      setMsg({ tone: "ok", text: summary && summary.emr_document_id ? `Filed in the EMR (document ${summary.emr_document_id}).` : "Sent to the EMR." });
    } catch (e) {
      const info = errInfo(e);
      if (info.status === 409 && info.code === "already_posted") setMsg({ tone: "ok", text: "Already in the EMR." });
      else setMsg({ tone: "error", text: (info.data && info.data.emr_error) || info.message || "Couldn't send to the EMR. Try again." });
    } finally {
      await refresh();
      setBusy("");
      setStepText("");
    }
  };

  const amend = async (reason) => {
    setBusy("amend");
    setDlgError("");
    try {
      const created = await internalApi.amend(detail.id, { reason });
      setAmendOpen(false);
      await refresh();
      if (created && created.id && onOpenSubmission) onOpenSubmission(created.id, { startEditing: true });
    } catch (e) {
      const info = errInfo(e);
      if (info.code === "already_amended") setDlgError("This form already has an amendment. Open it from the patient's staff forms.");
      else if (info.code === "not_posted") setDlgError("This form isn't in the EMR yet: edit it instead.");
      else setDlgError(info.message || "Couldn't create the amendment.");
    } finally {
      setBusy("");
    }
  };

  const doVoid = async (reason) => {
    setBusy("void");
    setDlgError("");
    try {
      await internalApi.voidForm(detail.id, { reason });
      setVoidOpen(false);
      setMsg({ tone: "ok", text: "The form was voided." });
      await refresh();
    } catch (e) {
      const info = errInfo(e);
      if (info.code === "not_author" || info.status === 403) setDlgError("Only the author or an admin can void this form.");
      else if (info.code === "locked") setDlgError("It's in the EMR: amend it instead.");
      else setDlgError(info.message || "Couldn't void the form.");
    } finally {
      setBusy("");
    }
  };

  const discard = async (draft) => {
    // eslint-disable-next-line no-alert
    if (typeof window !== "undefined" && window.confirm && !window.confirm(draft ? "Discard this draft? It can't be recovered." : "Discard the unsaved changes?")) return;
    setBusy("discard");
    setMsg(null);
    try {
      await internalApi.discard(detail.id, { edit_seq: staff.edit_seq || 0 });
      setMsg({ tone: "ok", text: draft ? "Draft discarded." : "Unsaved changes discarded." });
      await refresh();
    } catch (e) {
      const info = errInfo(e);
      setMsg({ tone: "error", text: info.code === "not_author" || info.status === 403 ? "Only the author or an admin can discard this draft." : info.message || "Couldn't discard." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fwi-actions" data-testid="internal-actions">
      <div className="fws-row">
        {show.edit && (
          <button type="button" className="fws-btn fws-btn-primary" disabled={!!busy} onClick={onEdit}>
            {staff.pending_edit ? "Continue editing" : "Edit"}
          </button>
        )}
        {showEmr && show.emr && (
          <button type="button" className="fws-btn fws-btn-accent" disabled={!!busy || !def} onClick={send}>
            {busy === "emr" ? stepText || "Sending…" : detail.emr_status === "failed" ? "Retry Send to EMR" : "Send to EMR"}
          </button>
        )}
        {showPrint && show.pdf && (
          <button type="button" className="fws-btn" disabled={!!busy || !def} onClick={() => pdf(false)}>
            {busy === "pdf" ? "Making PDF…" : "Save PDF"}
          </button>
        )}
        {showPrint && show.print && (
          <button type="button" className="fws-btn" disabled={!!busy || !def} onClick={() => pdf(true)}>
            {busy === "print" ? "Preparing…" : "Print"}
          </button>
        )}
      </div>
      {(show.amend || show.void || show.discardChanges || show.discardDraft) && (
        <div className="fws-row" style={{ marginTop: 8 }}>
          {show.amend && (
            <button type="button" className="fws-btn fws-btn-sm" disabled={!!busy} onClick={() => { setDlgError(""); setAmendOpen(true); }}>
              {staff.kind === "staff_sections" ? "Amend staff sections" : "Amend"}
            </button>
          )}
          {show.discardChanges && (
            <button type="button" className="fws-btn fws-btn-sm" disabled={!!busy} onClick={() => discard(false)}>Discard changes</button>
          )}
          {show.discardDraft && (
            <button type="button" className="fws-btn fws-btn-sm fws-btn-danger" disabled={!!busy} onClick={() => discard(true)}>Discard draft</button>
          )}
          {show.void && (
            <button type="button" className="fws-btn fws-btn-sm fws-btn-danger" disabled={!!busy} onClick={() => { setDlgError(""); setVoidOpen(true); }}>Void</button>
          )}
        </div>
      )}
      {LOCKED_EMR.includes(detail.emr_status) && (
        <p className="fws-small fws-muted" style={{ marginTop: 8 }} data-testid="fwi-locked-note">
          Sent to the EMR, so this form is locked.{show.amend ? " Use Amend to correct it." : ""}
        </p>
      )}
      {msg && <div className={`fws-alert fws-alert-${msg.tone}`} role={msg.tone === "error" ? "alert" : "status"} style={{ marginTop: 8 }}>{msg.text}</div>}
      <AmendDialog open={amendOpen} busy={busy === "amend"} error={dlgError} title={title} onClose={() => setAmendOpen(false)} onSubmit={amend} />
      <VoidDialog open={voidOpen} busy={busy === "void"} error={dlgError} title={title} onClose={() => setVoidOpen(false)} onSubmit={doVoid} />
    </div>
  );
}
